import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Typography, useMediaQuery } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions/transition';
import React, { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FerrureBox } from '../../../../../core/FerrureBox';
import { OeillereBox } from '../../../../../core/OeillereBox';
import {
    extractCourseCategory,
    extractGainMax,
    extractReculValue,
    filterReportsByDate,
    formatCategorieParticularite,
    formatDiscipline,
    formatReduction,
    getCorde,
    getPoidHandicapLabel,
} from '../../../../../utils/format';
import { usePronoCache } from '../../../providers/PronoCacheProvider';
import { Terrain } from '../../../services/enum';
import useGetCourses from '../../../services/use-get-courses';
import useGetPronos from '../../../services/use-get_pronos';
import { Header } from './Header';
import ReportDetailCard from './ReportDetailCard';
import { Report } from '../../../services/type';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="right" ref={ref} {...props} />;
});

const HistoryDialog: FC = () => {
    const { date, r, c, num } = useParams<{
        date: string;
        r: string;
        c: string;
        num: string;
    }>();
    const [selectedReport, setSelectedReport] = useState<Report | null>(null);

    const { data: program } = useGetCourses(date || '');
    const { data: pronos } = useGetPronos(date || '', Number(r), Number(c));
    const reunion = program?.reunions.find(
        (reu) => reu.numOfficiel === Number(r)
    );
    const course = reunion?.courses.find((cou) => cou.numOrdre === Number(c));
    const navigate = useNavigate();
    const prono = pronos?.find((p) => p.data.numero === Number(num));
    const otherHorses =
        pronos
            ?.filter((p) => p.data.numero !== Number(num))
            .map((p) => {
                return { nom: p.data.nom, num: p.data.numero };
            }) || [];

    const { recent30Days, recent6Months, olderReports } = filterReportsByDate(
        prono?.reports
    );

    const { toggleProno, hasProno } = usePronoCache();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

    if (!course || !reunion || !prono) {
        return null;
    }

    course.plafond = extractGainMax(course.conditions);
    course.recul = extractReculValue(course.conditions, course.plafond);
    const categorie = extractCourseCategory(course.conditions);
    const corde = getCorde(course.corde);

    const isFerreChanged =
        (prono?.reports?.length || 0) > 0 &&
        prono.data.deferre !== prono.reports[0].deferre;

    const handleClose = () => {
        navigate(-1);
    };
    return (
        prono && (
            <Dialog
                open={!!prono}
                TransitionComponent={Transition}
                onClose={handleClose}
                fullScreen={isMobile}
                maxWidth="sm"
            >
                <Box
                    bgcolor={'secondary.main'}
                    width={'80px'}
                    height={'70px'}
                    display={'flex'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        clipPath: 'polygon(0% 0%, 100% 0%, 80% 100%, 0% 100%)',
                    }}
                >
                    <Typography fontWeight={600} color={'white'} fontSize={26}>
                        {prono.data.numero}
                    </Typography>
                </Box>
                <DialogTitle
                    display={'flex'}
                    justifyContent={'flex-start'}
                    ml={7}
                    alignItems={'baseline'}
                    sx={{ pt: isMobile ? 0 : 1 }}
                >
                    <Box display={'flex'} flexDirection={'column'}>
                        <Box
                            display={'flex'}
                            flexDirection={'row'}
                            alignItems={'baseline'}
                            gap={1}
                            maxHeight={'33px'}
                            mt={isMobile ? 1 : 0}
                        >
                            <Typography
                                fontWeight={550}
                                color={'primary'}
                                fontSize={isMobile ? 18 : 22}
                                sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: isMobile ? '180px' : '300px',
                                }}
                            >
                                {prono.data.nom}
                            </Typography>
                            <FerrureBox
                                deferre={prono.data.deferre}
                                isSecondary={isFerreChanged}
                            />
                            <OeillereBox
                                oeillere={prono.data.oeilleres}
                                isSecondary={false}
                            />
                            {(prono.data.poids > 0 || prono.data.handicap > 0) &&
                                !isMobile && (
                                    <Typography variant="subtitle1">
                                        {getPoidHandicapLabel(
                                            prono.data.handicap,
                                            prono.data.poids
                                        )}
                                    </Typography>
                                )}
                            {(prono.performance.averageRed || 0) > 0 && !isMobile && (
                                <Typography variant="subtitle2" color={'primary'}>
                                    {formatReduction(prono.performance.averageRed || 0)}
                                </Typography>
                            )}
                        </Box>
                        <Box display={'flex'} flexDirection={'row'} gap={1}>
                            <Typography variant="subtitle1">
                                {formatDiscipline(course.discipline, isMobile)}
                            </Typography>
                            <Typography variant="subtitle1">{`${course.distance}m`}</Typography>
                            {corde.length > 0 && (
                                <Typography variant="subtitle1">{`(${corde})`}</Typography>
                            )}
                            {categorie && (
                                <Typography variant="subtitle1">
                                    {`cat: ${categorie}`}
                                </Typography>
                            )}
                            {!isMobile && (
                                <>
                                    <Typography variant="subtitle1">
                                        {`${course.montantPrix.toLocaleString('fr-FR')}€`}
                                    </Typography>
                                    <Box display={'flex'} flexDirection={'row'} gap={0.5}>
                                        <Typography variant="subtitle1">
                                            {formatCategorieParticularite(
                                                course.penetrometre?.intitule.includes('PSF')
                                                    ? Terrain.PSF_STANDARD
                                                    : course.typePiste
                                            )}
                                        </Typography>
                                        {course.typePiste && course.penetrometre?.valeurMesure && (
                                            <Typography variant="subtitle1">{`(${course.penetrometre?.valeurMesure})`}</Typography>
                                        )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Box>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            right: 0,
                            top: 38,
                            color: hasProno(prono, reunion, course)
                                ? 'secondary.main'
                                : 'primary.main',
                        }}
                        onClick={() => toggleProno(prono, reunion, course)}
                    >
                        {hasProno(prono, reunion, course) ? (
                            <BookmarkAddedIcon sx={{ fontSize: 34 }} />
                        ) : (
                            <BookmarkAddIcon sx={{ fontSize: 34 }} />
                        )}
                    </IconButton>
                </DialogTitle>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 5,
                        top: 4,
                        color: 'primary.main',
                    }}
                >
                    <CloseIcon />
                </IconButton>
                <DialogContent sx={{ height: '65vh', p: isMobile ? 1 : 2 }}>
                    <Header prono={prono} course={course} />
                    {prono.reports.length > 0 ? (
                        <Box display={'flex'} flexDirection={'column'} gap={2}>
                            <Typography
                                color="primary.main"
                                fontSize={18}
                                mt={1}
                                alignSelf={'flex-start'}
                                sx={{
                                    borderBottom: '3px solid',
                                    borderColor: 'secondary.main', // Couleur de la bordure
                                    pb: 0.1, // Ajuste l'espacement avec le texte
                                }}
                            >
                                2 derniers mois
                            </Typography>
                            <Box display={'flex'} flexDirection={'column'} gap={2}>
                                {recent30Days.map((r) => (
                                    <ReportDetailCard
                                        key={`${r.numPMU}-${r.heureDepart}`}
                                        reunion={reunion}
                                        report={r}
                                        prono={prono}
                                        course={course}
                                        categorie={categorie || undefined}
                                        selectedReport={selectedReport}
                                        setSelectedReport={setSelectedReport}
                                        otherHorses={otherHorses}
                                    />
                                ))}
                            </Box>
                            <Typography
                                color="primary.main"
                                fontSize={18}
                                mt={1}
                                alignSelf={'flex-start'}
                                sx={{
                                    borderBottom: '3px solid',
                                    borderColor: 'secondary.main', // Couleur de la bordure
                                    pb: 0.1, // Ajuste l'espacement avec le texte
                                }}
                            >
                                6 derniers mois
                            </Typography>
                            <Box display={'flex'} flexDirection={'column'} gap={2}>
                                {recent6Months.map((r) => (
                                    <ReportDetailCard
                                        key={`${r.numPMU}-${r.heureDepart}`}
                                        report={r}
                                        prono={prono}
                                        reunion={reunion}
                                        course={course}
                                        categorie={categorie || undefined}
                                        selectedReport={selectedReport}
                                        setSelectedReport={setSelectedReport}
                                        otherHorses={otherHorses}
                                    />
                                ))}
                            </Box>
                            <Typography
                                color="primary.main"
                                fontSize={18}
                                mt={1}
                                alignSelf={'flex-start'}
                                sx={{
                                    borderBottom: '3px solid',
                                    borderColor: 'secondary.main', // Couleur de la bordure
                                    pb: 0.1, // Ajuste l'espacement avec le texte
                                }}
                            >
                                Historique
                            </Typography>
                            <Box display={'flex'} flexDirection={'column'} gap={2}>
                                {olderReports.map((r) => (
                                    <ReportDetailCard
                                        key={`${r.numPMU}-${r.heureDepart}`}
                                        report={r}
                                        reunion={reunion}
                                        prono={prono}
                                        course={course}
                                        categorie={categorie || undefined}
                                        selectedReport={selectedReport}
                                        setSelectedReport={setSelectedReport}
                                        otherHorses={otherHorses}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            display={'flex'}
                            justifyContent={'center'}
                            alignItems={'center'}
                            sx={{ height: '40vh' }}
                        >
                            <Typography>Aucun historique disponnible</Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        )
    );
};

export default HistoryDialog;
